import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCalendarEvent,
  IconCamera,
  IconHistory,
  IconLogout,
  IconScanEye,
  IconSun,
  IconX,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { AsiLogo } from "../components/AsiLogo";
import { api, ApiError } from "../lib/api";
import {
  classifyBunch,
  OLC_UI_OPTIONS,
  MESOCARP_UI_OPTIONS,
  SOCKET_BUCKETS,
  socketBucketFor,
  type Olc,
  type Mesocarp,
  type RipenessClass,
} from "../lib/estate_ripeness_classifier";
import { clearSession, getSession } from "../lib/auth";

/**
 * Mandor mobile view · phone-first mirror of the v3 mockups.
 * Bottom tabs: Today · Census · Grade · History.
 *
 * Identity rule · this screen belongs to the MANDOR persona: every
 * name shown is the assignment's Mandor (from the schedule / gang
 * master), never the office account that happens to be signed in.
 *
 * Grade tab · MANUAL or AI photo mode. AI posts to
 * /v1/bunch-audits/ai; when the server has no OpenRouter key it
 * answers 501 and the UI falls back to manual — no fake numbers.
 */

interface Worker {
  id: string;
  name: string;
  workerType: string;
}
interface Tph {
  id: string;
  code: string;
  blockId: string;
}
interface Gang {
  id: string;
  code: string;
  name: string;
}
interface Division {
  id: string;
  code: string;
  name?: string | null;
}
interface Wristband {
  id: string;
  workerId: string;
  status: string;
}
interface AuditBatch {
  id: string;
  tphId: string;
  mandorWorkerId: string | null;
  gangId: string | null;
  mode: string;
  totalBunches: number;
  ripeCount: number;
  underripeCount: number;
  unripeCount: number;
  overripeCount: number;
  emptyCount: number;
  rottenCount: number;
  socketCount: number;
  exocarpColour: string | null;
  mesocarpColour: string | null;
  readyForHarvest: string | null;
  submittedByUserId: string | null;
  ophCode: string | null;
  auditDate: string;
  defectFlags?: Record<string, boolean> | null;
  rgcqRawSignals?: {
    per_bunch?: Array<{
      bunch_index: number;
      detached_sockets: number;
      olc: string | null;
      mesocarp: string | null;
      classified_as?: string;
    }>;
    rejection?: {
      at: string;
      reason: string;
      byUserId: string | null;
    };
    quality_state_counts?: {
      fresh?: number;
      underripe?: number;
      long_stalk?: number;
      old?: number;
      wet?: number;
      dirty?: number;
      dura?: number;
      parthenocarpy?: number;
      rotten?: number;
    };
  } | null;
}
interface Census {
  id: string;
  blockId: string;
  censusDate: string;
  ringOneCount: number;
  ringTwoCount: number;
  ringThreeCount: number;
}
interface Vehicle {
  id: string;
  plateNumber: string;
  defaultDriverWorkerId?: string | null;
}
interface Ramp {
  id: string;
  code: string;
}
interface Mill {
  id: string;
  name: string;
}
interface Pickup {
  id: string;
  driverWorkerId: string;
  tphId: string;
  ophBatchIds: string[];
  totalBunches: number;
  status: string;
  pickedUpAt: string;
  deliveredAt: string | null;
}
interface Spb {
  id: string;
  spbNumber: string;
  totalBunches: number;
  status: string;
  dispatchedAt: string | null;
}
interface PlanRow {
  line: {
    id: string;
    blockId: string;
    targetHarvestDate: string | null;
    estimatedBunches: number;
    estimatedKg: string;
    status: string;
    gangId: string | null;
    mandorWorkerId: string | null;
    harvesterWorkerIds: string[];
    carrierWorkerIds: string[];
  };
  block: {
    id: string;
    code: string;
    hectaresPlanted: string | null;
    divisionId: string;
  };
  mandorWorker: { name: string } | null;
}

// Schedule-line lifecycle (harvest_plan_line.status) — the plan's own
// approval/execution state, NOT the physical FFB pipeline (bunch
// audit → mech pickup → SPB dispatch), which each track their own
// status separately (see the Pickup / SPB tabs below).
const STATUS_DESC: Record<
  "ALL" | "DRAFT" | "CONFIRMED" | "IN_PROGRESS" | "DONE",
  string
> = {
  ALL: "All schedule statuses",
  DRAFT: "Target plan · not yet approved",
  CONFIRMED: "Approved · gang locked in, ready to execute",
  IN_PROGRESS: "Mandor started the harvest round today",
  DONE: "Round completed · actuals recorded",
};
export function MandorMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = getSession();
  // One phone shell, three field personas (v3 §1). In production each
  // person signs in with their own role; the switcher makes every
  // step testable from one dev login.
  const [role, setRole] = useState<"MANDOR" | "DRIVER" | "DISPATCH">(
    "MANDOR",
  );
  const [tab, setTab] = useState<
    "today" | "census" | "grade" | "history" | "pickup" | "dispatch"
  >("today");
  // Roster row "Details" button → worker id whose harvest detail
  // sheet is open. null = sheet closed.
  const [detailWorkerId, setDetailWorkerId] = useState<string | null>(null);
  const [detailFrom, setDetailFrom] = useState("");
  const [detailTo, setDetailTo] = useState("");
  // History tab "Block detail" · pick a block, see all schedule +
  // audit activity there instead of one harvester's attribution.
  const [blockPickerId, setBlockPickerId] = useState("");
  const [blockDetailId, setBlockDetailId] = useState<string | null>(null);
  const [blockFrom, setBlockFrom] = useState("");
  const [blockTo, setBlockTo] = useState("");
  const [blockStatusFilter, setBlockStatusFilter] = useState<
    "ALL" | "DRAFT" | "CONFIRMED" | "IN_PROGRESS" | "DONE"
  >("ALL");

  // Same endpoint the Harvesting page reads · staleTime 0 +
  // refetchOnMount so approving/editing there reflects here instantly
  // (and vice-versa) without a manual reload.
  const planQ = useQuery({
    queryKey: ["harvest-schedule-mobile"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: async () => {
      const d = new Date();
      const from = new Date(d.getTime() - 7 * 86400000).toISOString().slice(0, 10);
      const to = new Date(d.getTime() + 7 * 86400000).toISOString().slice(0, 10);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ""}/v1/harvest-schedule?from=${from}&to=${to}`,
      );
      if (!res.ok) throw new Error(`plan ${res.status}`);
      return (await res.json()) as { scheduled: PlanRow[] };
    },
  });
  // Same block list the Harvesting census form offers (all PLANTED
  // blocks) — not just blocks already on the schedule.
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () =>
      api.list<{ id: string; code: string; category: string }>(
        "/v1/blocks?limit=500",
      ),
  });
  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () => api.list<Worker>("/v1/workers?limit=500"),
  });
  const tphsQ = useQuery({
    queryKey: ["tphs"],
    queryFn: () => api.list<Tph>("/v1/tphs"),
  });
  const gangsQ = useQuery({
    queryKey: ["gangs"],
    queryFn: () => api.list<Gang>("/v1/gangs"),
  });
  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () => api.list<Division>("/v1/divisions"),
  });
  const wristbandsQ = useQuery({
    queryKey: ["nfc-wristbands"],
    queryFn: () => api.list<Wristband>("/v1/nfc-wristbands"),
  });
  // Same queryKeys the Delivery (Field Ops) and Harvesting pages use,
  // with staleTime 0 so an audit or census saved on either surface
  // shows on the other without a reload.
  const auditsQ = useQuery({
    queryKey: ["bunch-audits"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<AuditBatch>("/v1/bunch-audits"),
  });
  const censusHistQ = useQuery({
    queryKey: ["mandor-census-recent"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return api.list<Census>(
        `/v1/mandor-census?from=${d.toISOString().slice(0, 10)}`,
      );
    },
  });
  // Driver + Dispatch data · same keys the Delivery web page uses.
  const vehiclesQ = useQuery({
    queryKey: ["vehicles"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<Vehicle>("/v1/vehicles"),
  });
  const rampsQ = useQuery({
    queryKey: ["ramps"],
    queryFn: () => api.list<Ramp>("/v1/ramps"),
  });
  const millsQ = useQuery({
    queryKey: ["mills"],
    queryFn: () => api.list<Mill>("/v1/mills"),
  });
  const pickupsQ = useQuery({
    queryKey: ["mech-pickups"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<Pickup>("/v1/mech-pickups"),
  });
  const spbQ = useQuery({
    queryKey: ["spb"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<Spb>("/v1/spb"),
  });
  // Roster "Details" sheet · real OPH attribution history for the
  // selected worker (bunches picked, per TPH audit). Only fetches
  // when the sheet is open.
  const workerDetailQ = useQuery({
    queryKey: ["worker-harvest-detail", detailWorkerId, detailFrom, detailTo],
    enabled: !!detailWorkerId,
    staleTime: 0,
    queryFn: () => {
      const params = new URLSearchParams();
      if (detailFrom) params.set("from", detailFrom);
      if (detailTo) params.set("to", detailTo);
      const qs = params.toString();
      return api.get<{
        worker: Worker | null;
        range: { from: string; to: string };
        attributions: {
          id: string;
          bunches: number;
          auditDate: string;
          mode: string;
          ophCode: string | null;
          tphCode: string;
          blockCode: string;
        }[];
        totals: {
          today: number;
          last7d: number;
          last30d: number;
          rangeBunches: number;
          recordCount: number;
        };
      }>(`/v1/workers/${detailWorkerId}/harvest-detail${qs ? `?${qs}` : ""}`);
    },
  });
  // Block detail sheet · schedule-vs-actual + all TPH audits for one
  // block, in the picked date range (default: last 30 days).
  const blockDetailQ = useQuery({
    queryKey: ["block-harvest-detail", blockDetailId, blockFrom, blockTo],
    enabled: !!blockDetailId,
    staleTime: 0,
    queryFn: () => {
      const params = new URLSearchParams();
      if (blockFrom) params.set("from", blockFrom);
      if (blockTo) params.set("to", blockTo);
      const qs = params.toString();
      return api.get<{
        block: {
          id: string;
          code: string;
          hectaresPlanted: string | null;
          palmCount: number | null;
        };
        range: { from: string; to: string };
        scheduleLines: {
          id: string;
          targetHarvestDate: string | null;
          actualHarvestDate: string | null;
          estimatedBunches: number;
          actualBunches: number;
          status: string;
          varianceBunches: number | null;
          variancePct: number | null;
          varianceDays: number | null;
        }[];
        audits: {
          id: string;
          tphCode: string;
          auditDate: string;
          mode: string;
          totalBunches: number;
          ophCode: string | null;
          harvesters: { workerId: string; name: string; bunches: number }[];
        }[];
        totals: {
          estimatedBunches: number;
          actualBunches: number;
          auditedBunches: number;
          scheduleLineCount: number;
          auditCount: number;
        };
      }>(`/v1/blocks/${blockDetailId}/harvest-detail${qs ? `?${qs}` : ""}`);
    },
  });
  const setQuickRange = (days: number) => {
    const t = new Date();
    const to = t.toISOString().slice(0, 10);
    const from =
      days === 0
        ? to
        : new Date(t.getTime() - days * 86400000).toISOString().slice(0, 10);
    setDetailFrom(from);
    setDetailTo(to);
  };
  const filteredScheduleLines = (blockDetailQ.data?.data.scheduleLines ?? []).filter(
    (l) => blockStatusFilter === "ALL" || l.status === blockStatusFilter,
  );
  // TPH audits always show the full breakdown for the date range,
  // independent of the schedule status chips above — those chips
  // filter the schedule lines only. Audits have no plan_line_id to
  // filter by anyway (bunch_audit_batch only knows TPH + date).
  const filteredAudits = blockDetailQ.data?.data.audits ?? [];

  const workerById = useMemo(() => {
    const m = new Map<string, Worker>();
    for (const w of workersQ.data?.data ?? []) m.set(w.id, w);
    return m;
  }, [workersQ.data]);
  const blockCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of blocksQ.data?.data ?? []) m.set(b.id, b.code);
    return m;
  }, [blocksQ.data]);
  const plantedBlocks = useMemo(
    () => (blocksQ.data?.data ?? []).filter((b) => b.category === "PLANTED"),
    [blocksQ.data],
  );
  const tphCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tphsQ.data?.data ?? []) m.set(t.id, t.code);
    return m;
  }, [tphsQ.data]);
  const wristbandByWorker = useMemo(() => {
    const m = new Map<string, Wristband>();
    for (const w of wristbandsQ.data?.data ?? []) m.set(w.workerId, w);
    return m;
  }, [wristbandsQ.data]);
  const harvesters = (workersQ.data?.data ?? []).filter(
    (w) => w.workerType === "HARVESTER",
  );
  const drivers = (workersQ.data?.data ?? []).filter(
    (w) => w.workerType === "DRIVER",
  );
  const issuedCards = (auditsQ.data?.data ?? []).filter((a) => a.ophCode);
  const unissuedAudits = (auditsQ.data?.data ?? []).filter((a) => !a.ophCode);

  const active = (planQ.data?.scheduled ?? []).filter(
    (r) => r.line.status !== "DONE" && r.line.status !== "CANCELLED",
  );
  const assignment = active[0] ?? null;

  // Practical field ordering · the Mandor is standing in their
  // assigned block, so that block's TPHs list first.
  const sortedTphs = useMemo(() => {
    const assignedBlockId = assignment?.block.id;
    return [...(tphsQ.data?.data ?? [])].sort((a, b) => {
      const aHome = a.blockId === assignedBlockId ? 0 : 1;
      const bHome = b.blockId === assignedBlockId ? 0 : 1;
      return aHome - bHome || a.code.localeCompare(b.code);
    });
  }, [tphsQ.data, assignment]);

  // ── Identity · always the MANDOR, never the office login ──
  const mandorName =
    assignment?.mandorWorker?.name ??
    (assignment?.line.mandorWorkerId
      ? workerById.get(assignment.line.mandorWorkerId)?.name
      : undefined) ??
    "Mandor";
  const mandorFirst = mandorName
    .replace(/\s*\(.*\)\s*/, "")
    .split(" ")
    .slice(0, 2)
    .join(" ");
  const gang = (gangsQ.data?.data ?? []).find(
    (g) => g.id === assignment?.line.gangId,
  );
  const division = (divisionsQ.data?.data ?? []).find(
    (d) => d.id === assignment?.block.divisionId,
  );

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const yesterdayUnripe = (auditsQ.data?.data ?? [])
    .filter((a) => (a.auditDate ?? "").slice(0, 10) === yesterday)
    .reduce((n, a) => n + (a.unripeCount ?? 0), 0);

  // Crew · exactly the line's harvesters + carriers as shown on the
  // Harvesting schedule table — no invented pairings.
  const crew = useMemo(() => {
    if (!assignment) return [];
    return [
      ...assignment.line.harvesterWorkerIds.map((id) => ({
        id,
        role: "harvester" as const,
      })),
      ...assignment.line.carrierWorkerIds.map((id) => ({
        id,
        role: "carrier" as const,
      })),
    ];
  }, [assignment]);

  const [startMsg, setStartMsg] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const startHarvest = async () => {
    if (!assignment) return;
    setStartMsg(null);
    setStarting(true);
    try {
      await api.patch(`/v1/harvest-schedule/lines/${assignment.line.id}`, {
        status: "IN_PROGRESS",
      });
      setStartMsg("Harvest started ✓ · gang is on the block");
      qc.invalidateQueries({ queryKey: ["harvest-schedule-mobile"] });
    } catch (err) {
      setStartMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  };

  // ── Census state (keypad flow) ──
  const [censusBlockId, setCensusBlockId] = useState("");
  const [activeRing, setActiveRing] = useState<1 | 2 | 3>(1);
  const [rings, setRings] = useState<{ 1: number; 2: number; 3: number }>({
    1: 0,
    2: 0,
    3: 0,
  });
  const [palmsInspected, setPalmsInspected] = useState(0);
  const [censusMsg, setCensusMsg] = useState<string | null>(null);
  const censusBlock = censusBlockId || assignment?.block.id || "";
  const censusBlockCode =
    blockCodeById.get(censusBlock) ?? assignment?.block.code ?? "—";

  const keypadPress = (d: number) =>
    setRings((r) => ({
      ...r,
      [activeRing]: Math.min(99999, r[activeRing] * 10 + d),
    }));
  const keypadClear = () => setRings((r) => ({ ...r, [activeRing]: 0 }));

  const saveCensus = async () => {
    setCensusMsg(null);
    try {
      await api.post("/v1/mandor-census", {
        blockId: censusBlock,
        censusDate: today,
        ringOneCount: rings[1],
        ringTwoCount: rings[2],
        ringThreeCount: rings[3],
        palmsInspectedCount: palmsInspected || undefined,
        mandorWorkerId: assignment?.line.mandorWorkerId ?? undefined,
      });
      setCensusMsg("Census saved ✓");
      setRings({ 1: 0, 2: 0, 3: 0 });
      qc.invalidateQueries({ queryKey: ["mandor-census-recent"] });
    } catch (err) {
      setCensusMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ── Grade state (MANUAL / AI) ──
  const [gradeMode, setGradeMode] = useState<"MANUAL" | "AI">("MANUAL");
  const [gradeTphId, setGradeTphId] = useState("");
  const [gRipe, setGRipe] = useState(0);
  const [gUnderripe, setGUnderripe] = useState(0);
  const [gUnripe, setGUnripe] = useState(0);
  const [gradeMsg, setGradeMsg] = useState<string | null>(null);
  // RGCQ multi-head signals — captured either by AI or by Mandor observation.
  // Backing schema: bunch_audit_batch (migration 0015). Estate-side rule
  // per user directive: >= 5 sockets per bunch = READY for harvest.
  const [gExocarp, setGExocarp] = useState<
    "" | "black" | "dark_purple" | "reddish" | "orange"
  >("");
  const [gMesocarp, setGMesocarp] = useState<
    "" | "yellow" | "yellow_orange" | "orange"
  >("");
  const [gSockets, setGSockets] = useState(0);
  const [gDefects, setGDefects] = useState<Record<string, boolean>>({
    long_stalk: false,
    wet: false,
    dirty: false,
    old: false,
    underripe: false,
    unfresh: false,
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState<{
    batchId: string;
    total: number;
    ripe: number;
    underripe: number;
    unripe: number;
    overripe: number;
    empty: number;
    rotten: number;
    socketCount: number;
    exocarpColour: string | null;
    mesocarpColour: string | null;
    defectFlags: Record<string, boolean>;
    confidence: number;
    latencyMs: number;
  } | null>(null);
  // Object URL of the last uploaded photo — shown next to the AI reading so
  // the Mandor can eyeball whether the AI got it right before issuing the
  // OPH card. Revoked on next upload / reset to avoid leaks.
  const [aiPhotoUrl, setAiPhotoUrl] = useState<string | null>(null);
  // AI correction sheet · toggle + local edit state populated from aiResult.
  const [aiCorrectOpen, setAiCorrectOpen] = useState(false);
  const [aiCorrectSaving, setAiCorrectSaving] = useState(false);
  const [aiCorrectErr, setAiCorrectErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const saveManualGrade = async () => {
    setGradeMsg(null);
    const totalBunches = gRipe + gUnderripe + gUnripe;
    try {
      await api.post("/v1/bunch-audits", {
        tphId: gradeTphId,
        mandorWorkerId: assignment?.line.mandorWorkerId ?? null,
        mode: "MANUAL",
        totalBunches,
        ripeCount: gRipe,
        underripeCount: gUnderripe,
        unripeCount: gUnripe,
        // RGCQ signals (all optional server-side; sent when observed)
        exocarpColour: gExocarp || undefined,
        mesocarpColour: gMesocarp || undefined,
        socketCount: gSockets,
        defectFlags: gDefects,
      });
      const avg = totalBunches > 0 ? gSockets / totalBunches : 0;
      const readyMsg =
        gSockets === 0
          ? ""
          : avg >= 5
            ? " · READY (avg " + avg.toFixed(1) + " sockets/bunch)"
            : " · NOT READY (avg " + avg.toFixed(1) + " sockets/bunch, < 5)";
      setGradeMsg("Audit saved ✓" + readyMsg + " · issue OPH card next");
      setGRipe(0);
      setGUnderripe(0);
      setGUnripe(0);
      setGSockets(0);
      setGExocarp("");
      setGMesocarp("");
      setGDefects({
        long_stalk: false,
        wet: false,
        dirty: false,
        old: false,
        underripe: false,
        unfresh: false,
      });
      qc.invalidateQueries({ queryKey: ["bunch-audits"] });
    } catch (err) {
      setGradeMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  const runAiGrade = async (file: File) => {
    setGradeMsg(null);
    setAiResult(null);
    // Release the previous preview before showing the new one.
    setAiPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    // Soft warning · JSON body limit is 50 MB and base64 inflates ~1.35×
    // over the raw file, so anything above ~35 MB raw risks a 413. Warn
    // the user rather than let the upload silently hang.
    if (file.size > 35 * 1024 * 1024) {
      setGradeMsg(
        `Large image (${(file.size / 1024 / 1024).toFixed(1)} MB) · upload may be slow. Consider a lower-res mode.`,
      );
    }
    setAiBusy(true);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post<{
        batch: {
          id: string;
          exocarpColour: string | null;
          mesocarpColour: string | null;
          socketCount: number;
          defectFlags: Record<string, boolean> | null;
        };
        aggregate: {
          total: number;
          ripe: number;
          unripe: number;
          overripe: number;
          empty: number;
          rotten: number;
        };
        confidence: number;
        latencyMs: number;
      }>("/v1/bunch-audits/ai", {
        tphId: gradeTphId,
        mandorWorkerId: assignment?.line.mandorWorkerId ?? null,
        photoBase64: b64,
        mimeType: file.type || "image/jpeg",
      });
      const a = res.data.aggregate;
      const b = res.data.batch;
      setAiResult({
        batchId: b.id,
        total: a.total,
        ripe: a.ripe,
        // Server no longer emits an "underripe" bucket (4 classes only);
        // keep the field for backwards-compat with the existing UI.
        underripe: 0,
        unripe: a.unripe,
        overripe: a.overripe,
        empty: a.empty,
        rotten: a.rotten,
        socketCount: b.socketCount,
        exocarpColour: b.exocarpColour,
        mesocarpColour: b.mesocarpColour,
        defectFlags: b.defectFlags ?? {},
        confidence: res.data.confidence,
        latencyMs: res.data.latencyMs,
      });
      setAiCorrectOpen(false);
      setAiCorrectErr(null);
      setGradeMsg("AI audit saved ✓ · tap Correct if any count needs fixing");
      qc.invalidateQueries({ queryKey: ["bunch-audits"] });
    } catch (err) {
      setGradeMsg(err instanceof ApiError ? err.message : String(err));
      if (err instanceof ApiError && err.status === 501) {
        setGradeMode("MANUAL");
      }
    } finally {
      setAiBusy(false);
    }
  };

  const saveAiCorrection = async (patch: {
    ripe: number;
    underripe: number;
    unripe: number;
    overripe: number;
    empty: number;
    rotten: number;
    socketCount: number;
    exocarpColour: string | null;
    mesocarpColour: string | null;
    defectFlags: Record<string, boolean>;
  }) => {
    if (!aiResult) return;
    setAiCorrectErr(null);
    const sum =
      patch.ripe +
      patch.underripe +
      patch.unripe +
      patch.overripe +
      patch.empty +
      patch.rotten;
    if (sum !== aiResult.total) {
      setAiCorrectErr(
        `Counts sum to ${sum} but batch total is ${aiResult.total}.`,
      );
      return;
    }
    setAiCorrectSaving(true);
    try {
      await api.patch(`/v1/bunch-audits/${aiResult.batchId}`, {
        ripeCount: patch.ripe,
        underripeCount: patch.underripe,
        unripeCount: patch.unripe,
        overripeCount: patch.overripe,
        emptyCount: patch.empty,
        rottenCount: patch.rotten,
        socketCount: patch.socketCount,
        exocarpColour: patch.exocarpColour,
        mesocarpColour: patch.mesocarpColour,
        defectFlags: patch.defectFlags,
      });
      setAiResult({
        ...aiResult,
        ripe: patch.ripe,
        underripe: patch.underripe,
        unripe: patch.unripe,
        overripe: patch.overripe,
        empty: patch.empty,
        rotten: patch.rotten,
        socketCount: patch.socketCount,
        exocarpColour: patch.exocarpColour,
        mesocarpColour: patch.mesocarpColour,
        defectFlags: patch.defectFlags,
      });
      setAiCorrectOpen(false);
      setGradeMsg("Correction saved ✓");
      qc.invalidateQueries({ queryKey: ["bunch-audits"] });
    } catch (err) {
      setAiCorrectErr(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAiCorrectSaving(false);
    }
  };

  // ── NFC Delivery Card writer (Estate → Weighbridge handoff) ──
  // After grading + truck loaded, Mandor picks the audits that comprise
  // this truck load and generates a card. Card is scanned at the mill.
  const [nfcSelectedBatchIds, setNfcSelectedBatchIds] = useState<string[]>([]);
  const [nfcMsg, setNfcMsg] = useState<string | null>(null);
  const [nfcLastCard, setNfcLastCard] = useState<{
    cardCode: string;
    totalBunches: number;
  } | null>(null);
  const cardsQ = useQuery({
    queryKey: ["nfc-delivery-cards"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () =>
      api.list<{
        id: string;
        cardCode: string;
        status: string;
        totalBunches: number;
        writtenAt: string;
      }>("/v1/nfc-delivery-cards"),
  });
  const writeNfcCard = async () => {
    setNfcMsg(null);
    if (!assignment) {
      setNfcMsg("Need an active harvest assignment before writing a card.");
      return;
    }
    try {
      const res = await api.post<{
        cardCode: string;
        totalBunches: number;
        totalReadyForHarvest: number;
        totalPreRejected: number;
      }>("/v1/nfc-delivery-cards", {
        estateId: undefined,           // resolved server-side via block → division → estate later
        divisionId: assignment.block.divisionId,
        primaryBlockId: assignment.block.id,
        driverWorkerId: null,
        vehicleId: null,
        gangId: assignment.line.gangId ?? null,
        bunchAuditBatchIds: nfcSelectedBatchIds,
      } as unknown as Record<string, unknown>);
      setNfcLastCard({
        cardCode: res.data.cardCode,
        totalBunches: res.data.totalBunches,
      });
      setNfcMsg(
        `NFC card written ✓ · ${res.data.cardCode} · ${res.data.totalBunches} bunches`,
      );
      setNfcSelectedBatchIds([]);
      qc.invalidateQueries({ queryKey: ["nfc-delivery-cards"] });
    } catch (err) {
      setNfcMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ── OPH issue (Mandor closes the TPH loop on-phone) ──
  const [issueBatchId, setIssueBatchId] = useState("");
  const [issueHarvesterId, setIssueHarvesterId] = useState("");
  const [issueMsg, setIssueMsg] = useState<string | null>(null);
  const issueOph = async () => {
    setIssueMsg(null);
    const batch = unissuedAudits.find((a) => a.id === issueBatchId);
    try {
      const res = await api.post<{ ophCode: string }>("/v1/oph-cards/issue", {
        batchId: issueBatchId,
        attributions: [
          {
            harvesterWorkerId: issueHarvesterId,
            bunches: batch?.totalBunches ?? 0,
          },
        ],
      });
      setIssueMsg(`OPH issued ✓ · ${res.data.ophCode ?? ""}`);
      setIssueBatchId("");
      qc.invalidateQueries({ queryKey: ["bunch-audits"] });
    } catch (err) {
      setIssueMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ── Driver pickup state ──
  const [pkDriverId, setPkDriverId] = useState("");
  const [pkTphId, setPkTphId] = useState("");
  const [pkOphIds, setPkOphIds] = useState<string[]>([]);
  const [pkMsg, setPkMsg] = useState<string | null>(null);
  const startPickup = async () => {
    setPkMsg(null);
    try {
      await api.post("/v1/mech-pickups", {
        driverWorkerId: pkDriverId || drivers[0]?.id,
        tphId: pkTphId,
        ophBatchIds: pkOphIds,
      });
      setPkMsg("Pickup started ✓ · deliver at ramp when arrived");
      setPkOphIds([]);
      qc.invalidateQueries({ queryKey: ["mech-pickups"] });
    } catch (err) {
      setPkMsg(err instanceof ApiError ? err.message : String(err));
    }
  };
  const deliverPickup = async (id: string) => {
    setPkMsg(null);
    try {
      await api.post(`/v1/mech-pickups/${id}/deliver`, {});
      setPkMsg("Delivered at ramp ✓");
      qc.invalidateQueries({ queryKey: ["mech-pickups"] });
    } catch (err) {
      setPkMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ── Dispatch (SPB) state ──
  const [spbVehicleId, setSpbVehicleId] = useState("");
  const [spbRampId, setSpbRampId] = useState("");
  const [spbMillId, setSpbMillId] = useState("");
  const [spbOphIds, setSpbOphIds] = useState<string[]>([]);
  const [spbMsg, setSpbMsg] = useState<string | null>(null);
  const buildSpb = async () => {
    setSpbMsg(null);
    const vehicle = (vehiclesQ.data?.data ?? []).find(
      (v) => v.id === spbVehicleId,
    );
    try {
      const res = await api.post<{ spbNumber: string }>("/v1/spb", {
        vehicleId: spbVehicleId,
        driverWorkerId: vehicle?.defaultDriverWorkerId ?? null,
        fromRampId: spbRampId || null,
        toMillId: spbMillId,
        ophBatchIds: spbOphIds,
      });
      setSpbMsg(`SPB built ✓ · ${res.data.spbNumber}`);
      setSpbOphIds([]);
      qc.invalidateQueries({ queryKey: ["spb"] });
    } catch (err) {
      setSpbMsg(err instanceof ApiError ? err.message : String(err));
    }
  };
  const departSpb = async (id: string) => {
    setSpbMsg(null);
    try {
      await api.post(`/v1/spb/${id}/depart`, {});
      setSpbMsg("Departure recorded ✓ · truck en route to mill");
      qc.invalidateQueries({ queryKey: ["spb"] });
    } catch (err) {
      setSpbMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  const toggleChip = (
    id: string,
    list: string[],
    set: (v: string[]) => void,
  ) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const ophChips = (selected: string[], set: (v: string[]) => void) => (
    <div className="flex flex-wrap gap-1.5">
      {issuedCards.map((a) => (
        <button
          key={a.id}
          onClick={() => toggleChip(a.id, selected, set)}
          className={`rounded-lg border px-2.5 py-1.5 font-mono text-[11px] ${
            selected.includes(a.id)
              ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-800"
              : "border-border bg-surface-0"
          }`}
        >
          {a.ophCode}
        </button>
      ))}
      {!issuedCards.length && (
        <span className="text-[11px] text-text-secondary">
          no issued OPH cards yet — Mandor issues them after the TPH audit
        </span>
      )}
    </div>
  );

  const msgBox = (msg: string | null) =>
    msg && (
      <div
        className={`rounded-lg p-2.5 text-xs ${msg.includes("✓") ? "border border-emerald-300 bg-emerald-50 text-emerald-800" : "border border-amber-300 bg-amber-50 text-amber-900"}`}
      >
        {msg}
      </div>
    );

  const tabBtn = (
    t: typeof tab,
    label: string,
    icon: React.ReactNode,
  ) => (
    <button
      onClick={() => setTab(t)}
      className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] ${
        tab === t ? "font-semibold text-text-primary" : "text-text-secondary"
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const ringCard = (
    ring: 1 | 2 | 3,
    label: string,
    sub: string,
    big?: boolean,
  ) => (
    <button
      onClick={() => setActiveRing(ring)}
      className={`rounded-xl border p-3 text-center ${
        activeRing === ring
          ? "border-emerald-600 bg-white shadow-sm"
          : "border-border bg-surface-0"
      } ${big ? "col-span-2" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-wide text-text-secondary">
        {label} · {sub}
      </div>
      <div
        className={`tabular-nums font-semibold ${big ? "text-5xl text-emerald-700" : "text-2xl"}`}
      >
        {rings[ring]}
      </div>
      {big && (
        <div className="text-[10px] text-text-secondary">tap keypad to edit</div>
      )}
    </button>
  );

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-surface-1">
      {/* Header · mockup: "Smart Harvesting / Mandor Lim · Gang 04" */}
      <header className="flex items-center justify-between border-b border-border bg-surface-0 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <AsiLogo variant="mark" size={28} />
          <div>
            <div className="text-sm font-bold text-brand-charcoal">
              ASI <span className="text-brand-green-dark">Builder</span>
            </div>
            <div className="text-[11px] text-text-secondary">
              Smart Harvesting · Mandor {mandorFirst}
              {gang ? ` · ${gang.code}` : ""}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            clearSession();
            navigate("/login", { replace: true });
          }}
          className="rounded border border-border p-1.5 text-text-secondary"
          title="Sign out"
        >
          <IconLogout size={14} />
        </button>
      </header>

      {/* Persona switcher · in production each person has their own
          login; this makes all three field roles demoable on one device */}
      <div className="flex gap-1 border-b border-border bg-surface-0 px-4 py-2">
        {(
          [
            ["MANDOR", "Mandor"],
            ["DRIVER", "Driver"],
            ["DISPATCH", "Dispatch"],
          ] as const
        ).map(([r, label]) => (
          <button
            key={r}
            onClick={() => {
              setRole(r);
              setTab(r === "MANDOR" ? "today" : r === "DRIVER" ? "pickup" : "dispatch");
            }}
            className={`flex-1 rounded-lg py-1.5 text-[11px] font-semibold ${
              role === r
                ? "bg-text-primary text-white"
                : "bg-surface-1 text-text-secondary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <main className="flex-1 overflow-y-auto p-4 pb-24">
        {/* ── TODAY ── */}
        {tab === "today" && (
          <div className="space-y-4">
            <div>
              <div className="text-lg font-semibold">
                Selamat pagi, {mandorFirst}
              </div>
              <div className="text-xs text-text-secondary">
                {new Date().toLocaleDateString("en-MY", {
                  weekday: "long",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
            </div>

            {assignment ? (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-800">
                    Today's assignment
                  </div>
                  <span className="rounded bg-blue-700 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {assignment.line.status}
                  </span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  Block {assignment.block.code}
                </div>
                <div className="text-xs text-blue-800">
                  Division {division?.code ?? "—"}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-blue-900">
                  <div>
                    <span className="font-semibold">
                      {assignment.line.estimatedBunches}
                    </span>{" "}
                    bunches est.
                  </div>
                  <div>
                    <span className="font-semibold">
                      {Math.round(
                        Number(assignment.line.estimatedKg),
                      ).toLocaleString()}
                    </span>{" "}
                    kg est.
                  </div>
                  <div>
                    <span className="font-semibold">
                      {assignment.line.harvesterWorkerIds.length}H +{" "}
                      {assignment.line.carrierWorkerIds.length}C
                    </span>{" "}
                    team
                  </div>
                  <div>
                    Target ·{" "}
                    <span className="font-semibold">
                      {assignment.line.targetHarvestDate ?? "unscheduled"}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-surface-0 p-4 text-xs text-text-secondary">
                No active assignment. The planner approves the schedule on the
                Harvesting page.
              </div>
            )}

            {/* Team roster · exactly the crew on the schedule line */}
            {assignment && (
              <div className="rounded-xl border border-border bg-surface-0 p-4">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  Team roster · from the approved schedule
                </div>
                <div className="space-y-2">
                  {crew.map(({ id, role }) => {
                    const w = workerById.get(id);
                    const checkedIn = wristbandByWorker.has(id);
                    return (
                      <button
                        key={id}
                        onClick={() => setDetailWorkerId(id)}
                        className="flex w-full items-center gap-2 rounded-lg bg-surface-1 px-3 py-2 text-left text-xs active:bg-surface-2"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${checkedIn ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                        >
                          {(w?.name ?? "?").charAt(0)}
                        </span>
                        <div className="flex-1">
                          <div className="font-medium">
                            {w?.name ?? id.slice(0, 8)}
                          </div>
                          <div className="text-[10px] text-text-secondary">
                            {checkedIn
                              ? "NFC wristband active"
                              : "Not checked in yet"}
                          </div>
                        </div>
                        <span className="text-text-secondary">{role}</span>
                        <span className="ml-1 rounded border border-border px-2 py-1 text-[10px] font-medium text-text-primary">
                          Details
                        </span>
                      </button>
                    );
                  })}
                  {!crew.length && (
                    <div className="text-[11px] text-text-secondary">
                      No crew on this line yet — planner assigns the gang on
                      the Harvesting page.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Yesterday alert (mockup's amber banner) */}
            {yesterdayUnripe > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <span className="font-semibold">Yesterday:</span>{" "}
                {yesterdayUnripe} unripe flagged across TPH audits · review in
                History
              </div>
            )}

            {/* In-progress state · unmistakable banner, no dead button */}
            {assignment && assignment.line.status === "IN_PROGRESS" && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3.5 text-sm text-emerald-800">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-700 font-bold text-white">
                  ✓
                </span>
                <div>
                  <div className="font-semibold">Harvest in progress</div>
                  <div className="text-[11px]">
                    Block {assignment.block.code} · record actuals from the
                    office when the round ends
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons (mockup: Start harvest · Log census) */}
            {assignment && (
              <div className="grid grid-cols-2 gap-3">
                {assignment.line.status !== "IN_PROGRESS" && (
                  <button
                    onClick={startHarvest}
                    disabled={starting}
                    className="rounded-xl bg-emerald-700 py-3.5 text-sm font-bold text-white active:bg-emerald-800 disabled:opacity-60"
                  >
                    {starting ? "Starting…" : "Start harvest"}
                  </button>
                )}
                <button
                  onClick={() => setTab("census")}
                  className={`rounded-xl border border-border bg-surface-0 py-3.5 text-sm font-semibold active:bg-surface-2 ${assignment.line.status === "IN_PROGRESS" ? "col-span-2" : ""}`}
                >
                  Log census
                </button>
              </div>
            )}
            {startMsg && (
              <div
                className={`rounded-lg p-2.5 text-xs ${startMsg.includes("✓") ? "border border-emerald-300 bg-emerald-50 text-emerald-800" : "border border-red-300 bg-red-50 text-red-800"}`}
              >
                {startMsg}
              </div>
            )}
          </div>
        )}

        {/* ── CENSUS ── */}
        {tab === "census" && (
          <div className="space-y-3">
            <div>
              <div className="text-base font-semibold">Ring census</div>
              <div className="text-[11px] text-text-secondary">
                Block {censusBlockCode} · in-field
              </div>
            </div>
            <div className="rounded-lg bg-surface-2 p-3 text-[11px] text-text-secondary">
              Walk through the block · count ripening rings on the palms you
              sample · save when done. AI uses these to plan next harvest.
            </div>

            <select
              value={censusBlock}
              onChange={(e) => setCensusBlockId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
            >
              <option value="">— select block —</option>
              {plantedBlocks.map((b) => (
                <option key={b.id} value={b.id}>
                  Block {b.code}
                </option>
              ))}
            </select>

            {ringCard(1, "Ring 1", "ready to harvest", true)}
            <div className="grid grid-cols-2 gap-3">
              {ringCard(2, "Ring 2", "next week")}
              {ringCard(3, "Ring 3", "2+ weeks")}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button
                  key={d}
                  onClick={() => keypadPress(d)}
                  className="rounded-xl border border-border bg-surface-0 py-4 text-xl font-semibold active:bg-surface-2"
                >
                  {d}
                </button>
              ))}
              <button
                onClick={keypadClear}
                className="rounded-xl border border-border bg-surface-0 py-4 text-sm text-text-secondary active:bg-surface-2"
              >
                C
              </button>
              <button
                onClick={() => keypadPress(0)}
                className="rounded-xl border border-border bg-surface-0 py-4 text-xl font-semibold active:bg-surface-2"
              >
                0
              </button>
              <button
                onClick={saveCensus}
                disabled={!censusBlock}
                className="rounded-xl bg-emerald-700 py-4 text-sm font-bold text-white active:bg-emerald-800 disabled:opacity-40"
              >
                SAVE
              </button>
            </div>

            <label className="flex items-center justify-between rounded-lg border border-border bg-surface-0 px-3 py-2 text-xs">
              Palms inspected
              <input
                type="number"
                min={0}
                value={palmsInspected}
                onChange={(e) => setPalmsInspected(Number(e.target.value) || 0)}
                className="w-24 rounded border border-border px-2 py-1 text-right"
              />
            </label>

            {censusMsg && (
              <div
                className={`rounded-lg p-2.5 text-xs ${censusMsg.includes("✓") ? "border border-emerald-300 bg-emerald-50 text-emerald-800" : "border border-red-300 bg-red-50 text-red-800"}`}
              >
                {censusMsg}
              </div>
            )}
          </div>
        )}

        {/* ── GRADE ── */}
        {tab === "grade" && (
          <div className="space-y-3">
            <div>
              <div className="text-base font-semibold">Bunch audit</div>
              <div className="text-[11px] text-text-secondary">
                {gradeTphId
                  ? `TPH ${tphCodeById.get(gradeTphId) ?? ""}`
                  : "select a TPH"}{" "}
                · Mandor {mandorFirst}
              </div>
            </div>

            <div className="flex rounded-xl border border-border bg-surface-0 p-1">
              <button
                onClick={() => setGradeMode("MANUAL")}
                className={`flex-1 rounded-lg py-2 text-xs font-semibold ${gradeMode === "MANUAL" ? "bg-text-primary text-white" : "text-text-secondary"}`}
              >
                Manual count
              </button>
              <button
                onClick={() => setGradeMode("AI")}
                className={`flex-1 rounded-lg py-2 text-xs font-semibold ${gradeMode === "AI" ? "bg-emerald-700 text-white" : "text-text-secondary"}`}
              >
                AI photo grade
              </button>
            </div>

            <select
              value={gradeTphId}
              onChange={(e) => setGradeTphId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
            >
              <option value="">— select TPH —</option>
              {sortedTphs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code}
                </option>
              ))}
            </select>

            {gradeMode === "MANUAL" ? (
              <>
                {(
                  [
                    ["Ripe", gRipe, setGRipe],
                    ["Underripe", gUnderripe, setGUnderripe],
                    ["Unripe", gUnripe, setGUnripe],
                  ] as const
                ).map(([label, val, set]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between rounded-xl border border-border bg-surface-0 px-4 py-3"
                  >
                    <div className="text-sm font-medium">{label}</div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => set(Math.max(0, val - 1))}
                        className="h-9 w-9 rounded-full border border-border text-lg active:bg-surface-2"
                      >
                        −
                      </button>
                      <span className="w-10 text-center text-xl font-semibold tabular-nums">
                        {val}
                      </span>
                      <button
                        onClick={() => set(val + 1)}
                        className="h-9 w-9 rounded-full border border-border text-lg active:bg-surface-2"
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
                {/* ── RGCQ signals · compound-matrix inputs ────────
                    Estate-side rule per MPOB spec:
                      ≥5 sockets per bunch (avg) = READY for harvest
                      < 5 = NOT READY (unripe indicator)
                    Optional in MANUAL mode; auto-filled by AI mode. */}
                <div className="rounded-xl border border-border bg-surface-1 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                      RGCQ compound signals
                    </div>
                    {(() => {
                      const total = gRipe + gUnderripe + gUnripe;
                      const avg = total > 0 ? gSockets / total : 0;
                      if (gSockets === 0) return null;
                      const ready = avg >= 5;
                      return (
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            ready
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {ready ? "READY" : "NOT READY"} · {avg.toFixed(1)}/bunch
                        </span>
                      );
                    })()}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[10px] text-text-secondary">
                      Exocarp colour
                      <select
                        value={gExocarp}
                        onChange={(e) =>
                          setGExocarp(e.target.value as typeof gExocarp)
                        }
                        className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                      >
                        <option value="">— pick —</option>
                        <option value="black">Black</option>
                        <option value="dark_purple">Dark purple</option>
                        <option value="reddish">Reddish</option>
                        <option value="orange">Orange</option>
                      </select>
                    </label>
                    <label className="text-[10px] text-text-secondary">
                      Mesocarp (if cut)
                      <select
                        value={gMesocarp}
                        onChange={(e) =>
                          setGMesocarp(e.target.value as typeof gMesocarp)
                        }
                        className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                      >
                        <option value="">— pick —</option>
                        <option value="yellow">Yellow</option>
                        <option value="yellow_orange">Yellow-orange</option>
                        <option value="orange">Orange</option>
                      </select>
                    </label>
                  </div>

                  <label className="mt-2 block text-[10px] text-text-secondary">
                    Detached fruitlets / sockets (total across pile)
                    <div className="mt-0.5 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setGSockets(Math.max(0, gSockets - 5))}
                        className="h-8 w-8 rounded-full border border-border text-sm"
                      >
                        −5
                      </button>
                      <input
                        type="number"
                        min={0}
                        value={gSockets}
                        onChange={(e) =>
                          setGSockets(Math.max(0, Number(e.target.value) || 0))
                        }
                        className="flex-1 rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-center text-sm font-semibold"
                      />
                      <button
                        type="button"
                        onClick={() => setGSockets(gSockets + 5)}
                        className="h-8 w-8 rounded-full border border-border text-sm"
                      >
                        +5
                      </button>
                    </div>
                  </label>

                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {(
                      [
                        ["long_stalk", "Long stalk"],
                        ["wet", "Wet"],
                        ["dirty", "Dirty"],
                        ["old", "Old"],
                        ["underripe", "Underripe"],
                        ["unfresh", "Unfresh"],
                      ] as const
                    ).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setGDefects((d) => ({ ...d, [key]: !d[key] }))
                        }
                        className={`rounded-lg border px-1 py-1.5 text-[10px] font-semibold ${
                          gDefects[key]
                            ? "border-amber-500 bg-amber-50 text-amber-800"
                            : "border-border bg-surface-0 text-text-secondary"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={saveManualGrade}
                  disabled={!gradeTphId || gRipe + gUnderripe + gUnripe <= 0}
                  className="w-full rounded-xl bg-text-primary py-3.5 text-sm font-bold text-white disabled:opacity-40"
                >
                  Save audit ({gRipe + gUnderripe + gUnripe} bunches)
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={!gradeTphId || aiBusy}
                  className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-emerald-600 bg-emerald-50 py-8 text-emerald-800 disabled:opacity-40"
                >
                  <IconCamera size={32} />
                  <span className="text-sm font-semibold">
                    {aiBusy ? "Grading photo…" : "Photograph the TPH pile"}
                  </span>
                  <span className="text-[10px]">
                    AI counts bunches + grades ripeness · you can correct after
                  </span>
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void runAiGrade(f);
                    e.target.value = "";
                  }}
                />
                {/* Preview the just-uploaded photo while AI is analysing
                    and after it returns — so the Mandor can eyeball the
                    scan against the counts before issuing the OPH card. */}
                {aiPhotoUrl && (aiBusy || aiResult) && (
                  <div className="overflow-hidden rounded-xl border border-emerald-300 bg-white">
                    <div className="relative">
                      <img
                        src={aiPhotoUrl}
                        alt="Scanned TPH pile"
                        className="block w-full max-h-64 object-cover"
                      />
                      {aiBusy && (
                        <div className="absolute inset-0 flex items-center justify-center bg-emerald-900/40 text-xs font-semibold text-white">
                          Analysing…
                        </div>
                      )}
                      {aiResult && !aiBusy && (
                        <div className="absolute left-2 top-2 rounded bg-emerald-700/95 px-2 py-1 text-[10px] font-bold text-white">
                          AI · {aiResult.total} bunches ·{" "}
                          {(aiResult.confidence * 100).toFixed(0)}%
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {aiResult && (
                  <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
                    <div className="text-center text-4xl font-bold text-emerald-800 tabular-nums">
                      {aiResult.total}
                    </div>
                    <div className="text-center text-[10px] text-emerald-700">
                      bunches · {(aiResult.confidence * 100).toFixed(0)}%
                      confidence · {(aiResult.latencyMs / 1000).toFixed(1)}s
                    </div>
                    <div className="mt-2 flex justify-center gap-3 text-xs">
                      <span className="text-emerald-800">
                        ● Ripe {aiResult.ripe}
                      </span>
                      <span className="text-amber-700">
                        ● Underripe {aiResult.underripe}
                      </span>
                      <span className="text-slate-600">
                        ● Unripe {aiResult.unripe}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAiCorrectOpen((v) => !v)}
                      className="mt-3 w-full rounded-lg border border-emerald-600 bg-white py-1.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
                    >
                      {aiCorrectOpen ? "Cancel correction" : "Correct AI grading"}
                    </button>
                  </div>
                )}
                {aiResult && aiCorrectOpen && (
                  <AiCorrectionSheet
                    ai={aiResult}
                    saving={aiCorrectSaving}
                    err={aiCorrectErr}
                    onSave={saveAiCorrection}
                  />
                )}
              </>
            )}

            {gradeMsg && (
              <div
                className={`rounded-lg p-2.5 text-xs ${gradeMsg.includes("✓") ? "border border-emerald-300 bg-emerald-50 text-emerald-800" : "border border-amber-300 bg-amber-50 text-amber-900"}`}
              >
                {gradeMsg}
              </div>
            )}

            {/* Issue OPH card · closes the TPH loop on-phone */}
            {unissuedAudits.length > 0 && (
              <div className="rounded-xl border border-border bg-surface-0 p-4">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  Issue OPH card · {unissuedAudits.length} audit(s) pending
                </div>
                <div className="space-y-2">
                  <select
                    value={issueBatchId}
                    onChange={(e) => setIssueBatchId(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
                  >
                    <option value="">— pick audit —</option>
                    {unissuedAudits.map((a) => (
                      <option key={a.id} value={a.id}>
                        {tphCodeById.get(a.tphId) ?? "TPH"} · {a.totalBunches}{" "}
                        bunches · {a.mode}
                      </option>
                    ))}
                  </select>
                  <select
                    value={issueHarvesterId}
                    onChange={(e) => setIssueHarvesterId(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
                  >
                    <option value="">— attribute to harvester —</option>
                    {harvesters.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={issueOph}
                    disabled={!issueBatchId || !issueHarvesterId}
                    className="w-full rounded-xl bg-emerald-700 py-3 text-sm font-bold text-white disabled:opacity-40"
                  >
                    Issue OPH card
                  </button>
                  {msgBox(issueMsg)}
                </div>
              </div>
            )}

            {/* ── NFC Delivery Card writer ─────────────────────────
                After grading + truck loaded, Mandor picks the audits
                that make up this truck load and writes a card. The
                card holds provenance + grading summary; scanned at
                the mill weighbridge to prefill the intake ticket. */}
            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  Write NFC Delivery Card · truck loaded
                </div>
                <span className="text-[10px] text-text-secondary">
                  {(auditsQ.data?.data ?? []).length} audits today
                </span>
              </div>
              <div className="mb-2 text-[11px] text-text-secondary">
                Tap audits to include in this truck load. Card auto-fills
                block, division & driver from your assignment.
              </div>

              <div className="mb-3 flex flex-wrap gap-1.5">
                {(auditsQ.data?.data ?? []).slice(0, 12).map((a) => {
                  const picked = nfcSelectedBatchIds.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() =>
                        setNfcSelectedBatchIds((prev) =>
                          picked
                            ? prev.filter((x) => x !== a.id)
                            : [...prev, a.id],
                        )
                      }
                      className={`rounded-lg border px-2 py-1.5 text-[10px] font-semibold ${
                        picked
                          ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                          : "border-border bg-surface-1 text-text-secondary"
                      }`}
                    >
                      {a.ophCode ?? (tphCodeById.get(a.tphId) ?? "TPH")}
                      {" · "}
                      {a.totalBunches}b
                    </button>
                  );
                })}
                {!(auditsQ.data?.data ?? []).length && (
                  <span className="text-[11px] text-text-secondary">
                    No audits yet — run a grade above first.
                  </span>
                )}
              </div>

              <button
                onClick={writeNfcCard}
                disabled={!nfcSelectedBatchIds.length || !assignment}
                className="w-full rounded-xl bg-emerald-700 py-3 text-sm font-bold text-white disabled:opacity-40"
              >
                Write NFC card ({nfcSelectedBatchIds.length} audit
                {nfcSelectedBatchIds.length === 1 ? "" : "s"})
              </button>
              {msgBox(nfcMsg)}

              {nfcLastCard && (
                <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-800">
                    Ready for weighbridge
                  </div>
                  <div className="mt-1 font-mono text-sm font-bold text-emerald-900">
                    {nfcLastCard.cardCode}
                  </div>
                  <div className="text-[10px] text-emerald-800">
                    {nfcLastCard.totalBunches} bunches · Driver hands card at
                    mill gate
                  </div>
                </div>
              )}

              {(cardsQ.data?.data ?? []).length > 0 && (
                <div className="mt-3 border-t border-border pt-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                    Recent cards
                  </div>
                  <div className="space-y-1">
                    {(cardsQ.data?.data ?? []).slice(0, 5).map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between rounded bg-surface-1 px-2 py-1 text-[10px]"
                      >
                        <span className="font-mono">{c.cardCode}</span>
                        <span className="tabular-nums">{c.totalBunches}b</span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                            c.status === "SCANNED"
                              ? "bg-emerald-100 text-emerald-800"
                              : c.status === "WRITTEN"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Recent bunch audits · mirror of Smart Grading table ── */}
            <MandorAuditsList
              audits={auditsQ.data?.data ?? []}
              tphCodeById={tphCodeById}
              workerById={workerById}
              harvesterIds={assignment?.line.harvesterWorkerIds ?? []}
              currentUserId={session?.user.id ?? null}
              onRefresh={() =>
                qc.invalidateQueries({ queryKey: ["bunch-audits"] })
              }
            />
          </div>
        )}

        {/* ── DRIVER · PICKUP (TPH → ramp) ── */}
        {tab === "pickup" && (
          <div className="space-y-3">
            <div>
              <div className="text-base font-semibold">Mechanization pickup</div>
              <div className="text-[11px] text-text-secondary">
                Scan OPH cards at the TPH · deliver the load at the ramp
              </div>
            </div>

            <select
              value={pkDriverId || drivers[0]?.id || ""}
              onChange={(e) => setPkDriverId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
            >
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={pkTphId}
              onChange={(e) => setPkTphId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
            >
              <option value="">— at which TPH? —</option>
              {sortedTphs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code}
                </option>
              ))}
            </select>

            <div className="rounded-xl border border-border bg-surface-0 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Tap to scan OPH cards onto the load
              </div>
              {ophChips(pkOphIds, setPkOphIds)}
            </div>

            <button
              onClick={startPickup}
              disabled={!pkTphId || !pkOphIds.length}
              className="w-full rounded-xl bg-emerald-700 py-3.5 text-sm font-bold text-white disabled:opacity-40"
            >
              Start pickup ({pkOphIds.length} OPH)
            </button>
            {msgBox(pkMsg)}

            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                My loads
              </div>
              <div className="space-y-2">
                {(pickupsQ.data?.data ?? []).map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2.5 text-xs"
                  >
                    <div>
                      <div className="font-mono font-medium">
                        {tphCodeById.get(p.tphId) ?? "TPH"} ·{" "}
                        {Array.isArray(p.ophBatchIds) ? p.ophBatchIds.length : 0}{" "}
                        OPH · {p.totalBunches} bunches
                      </div>
                      <div className="text-[10px] text-text-secondary">
                        {p.status} · {(p.pickedUpAt ?? "").slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                    {p.status !== "DELIVERED" && (
                      <button
                        onClick={() => void deliverPickup(p.id)}
                        className="rounded-lg bg-emerald-700 px-3 py-2 text-[11px] font-bold text-white"
                      >
                        Deliver at ramp
                      </button>
                    )}
                  </div>
                ))}
                {!pickupsQ.data?.data.length && (
                  <div className="text-[11px] text-text-secondary">
                    No pickups yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── DISPATCH · SPB (ramp → mill) ── */}
        {tab === "dispatch" && (
          <div className="space-y-3">
            <div>
              <div className="text-base font-semibold">SPB dispatch</div>
              <div className="text-[11px] text-text-secondary">
                Scan OPHs onto the mill truck · issue SPB · record departure
              </div>
            </div>

            <select
              value={spbVehicleId}
              onChange={(e) => setSpbVehicleId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
            >
              <option value="">— vehicle —</option>
              {(vehiclesQ.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plateNumber}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={spbRampId}
                onChange={(e) => setSpbRampId(e.target.value)}
                className="rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
              >
                <option value="">— ramp —</option>
                {(rampsQ.data?.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code}
                  </option>
                ))}
              </select>
              <select
                value={spbMillId}
                onChange={(e) => setSpbMillId(e.target.value)}
                className="rounded-lg border border-border bg-surface-0 px-3 py-2.5 text-sm"
              >
                <option value="">— mill —</option>
                {(millsQ.data?.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-xl border border-border bg-surface-0 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Tap to scan OPHs onto the truck
              </div>
              {ophChips(spbOphIds, setSpbOphIds)}
            </div>

            <button
              onClick={buildSpb}
              disabled={!spbVehicleId || !spbMillId || !spbOphIds.length}
              className="w-full rounded-xl bg-text-primary py-3.5 text-sm font-bold text-white disabled:opacity-40"
            >
              Build SPB ({spbOphIds.length} OPH)
            </button>
            {msgBox(spbMsg)}

            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Today's SPBs
              </div>
              <div className="space-y-2">
                {(spbQ.data?.data ?? []).map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2.5 text-xs"
                  >
                    <div>
                      <div className="font-mono font-medium">{s.spbNumber}</div>
                      <div className="text-[10px] text-text-secondary">
                        {s.totalBunches} bunches · {s.status}
                      </div>
                    </div>
                    {s.status === "DRAFT" && (
                      <button
                        onClick={() => void departSpb(s.id)}
                        className="rounded-lg bg-emerald-700 px-3 py-2 text-[11px] font-bold text-white"
                      >
                        Record departure
                      </button>
                    )}
                  </div>
                ))}
                {!spbQ.data?.data.length && (
                  <div className="text-[11px] text-text-secondary">
                    No SPBs yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── HISTORY ── */}
        {tab === "history" && (
          <div className="space-y-4">
            <div className="text-base font-semibold">History</div>

            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Ring censuses · last 30 days
              </div>
              <div className="space-y-1.5">
                {(censusHistQ.data?.data ?? []).slice(0, 15).map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2 text-xs"
                  >
                    <span className="font-mono font-medium">
                      Block {blockCodeById.get(c.blockId) ?? c.blockId.slice(0, 6)}
                    </span>
                    <span className="tabular-nums text-text-secondary">
                      {c.ringOneCount} / {c.ringTwoCount} / {c.ringThreeCount}
                    </span>
                    <span className="text-text-secondary">{c.censusDate}</span>
                  </div>
                ))}
                {!censusHistQ.data?.data.length && (
                  <div className="text-[11px] text-text-secondary">
                    No censuses yet.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                TPH audits
              </div>
              <div className="space-y-1.5">
                {(auditsQ.data?.data ?? []).slice(0, 15).map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2 text-xs"
                  >
                    <span className="font-mono">
                      {a.ophCode ?? tphCodeById.get(a.tphId) ?? "—"}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${a.mode === "AI" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}
                    >
                      {a.mode}
                    </span>
                    <span className="tabular-nums">
                      {a.totalBunches} bunches
                    </span>
                    <span className="text-text-secondary">
                      {(a.auditDate ?? "").slice(0, 10)}
                    </span>
                  </div>
                ))}
                {!auditsQ.data?.data.length && (
                  <div className="text-[11px] text-text-secondary">
                    No audits yet.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface-0 p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Block detail · schedule vs actual, all audits
              </div>
              <div className="flex gap-2">
                <select
                  value={blockPickerId}
                  onChange={(e) => setBlockPickerId(e.target.value)}
                  className="flex-1 rounded-lg border border-border bg-surface-0 px-3 py-2 text-sm"
                >
                  <option value="">— select block —</option>
                  {plantedBlocks.map((b) => (
                    <option key={b.id} value={b.id}>
                      Block {b.code}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setBlockDetailId(blockPickerId);
                    setBlockStatusFilter("ALL");
                  }}
                  disabled={!blockPickerId}
                  className="rounded-lg bg-text-primary px-4 py-2 text-xs font-bold text-white disabled:opacity-40"
                >
                  View
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom tabs · scoped to the active persona */}
      <nav className="fixed inset-x-0 bottom-0 mx-auto flex max-w-md border-t border-border bg-surface-0">
        {role === "MANDOR" && (
          <>
            {tabBtn("today", "Today", <IconSun size={18} />)}
            {tabBtn("census", "Census", <IconCalendarEvent size={18} />)}
            {tabBtn("grade", "Grade", <IconScanEye size={18} />)}
            {tabBtn("history", "History", <IconHistory size={18} />)}
          </>
        )}
        {role === "DRIVER" && (
          <>
            {tabBtn("pickup", "Pickup", <IconSun size={18} />)}
            {tabBtn("history", "History", <IconHistory size={18} />)}
          </>
        )}
        {role === "DISPATCH" && (
          <>
            {tabBtn("dispatch", "SPB", <IconSun size={18} />)}
            {tabBtn("history", "History", <IconHistory size={18} />)}
          </>
        )}
      </nav>

      {/* Roster "Details" bottom sheet · real OPH attribution history */}
      {detailWorkerId && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={() => setDetailWorkerId(null)}
        >
          <div
            className="mx-auto max-h-[80vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface-0 p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-base font-semibold">
                  {workerDetailQ.data?.data.worker?.name ??
                    workerById.get(detailWorkerId)?.name ??
                    "Worker"}
                </div>
                <div className="text-[11px] text-text-secondary">
                  {workerDetailQ.data?.data.worker?.workerType ??
                    workerById.get(detailWorkerId)?.workerType ??
                    ""}{" "}
                  · Harvesting details
                </div>
              </div>
              <button
                onClick={() => setDetailWorkerId(null)}
                className="rounded p-1.5 text-text-secondary active:bg-surface-2"
              >
                <IconX size={18} />
              </button>
            </div>

            {workerDetailQ.isLoading && (
              <div className="py-6 text-center text-xs text-text-secondary">
                Loading…
              </div>
            )}

            {workerDetailQ.data && (
              <>
                <div className="mb-2 flex gap-1.5">
                  {(
                    [
                      ["Today", 0],
                      ["7d", 7],
                      ["30d", 30],
                    ] as const
                  ).map(([label, days]) => (
                    <button
                      key={label}
                      onClick={() => setQuickRange(days)}
                      className="flex-1 rounded-lg border border-border bg-surface-1 py-1.5 text-[11px] font-semibold active:bg-surface-2"
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <label className="text-[10px] text-text-secondary">
                    From
                    <input
                      type="date"
                      value={detailFrom || workerDetailQ.data.data.range.from}
                      onChange={(e) => setDetailFrom(e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-[10px] text-text-secondary">
                    To
                    <input
                      type="date"
                      value={detailTo || workerDetailQ.data.data.range.to}
                      onChange={(e) => setDetailTo(e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                    />
                  </label>
                </div>
                <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <div className="text-2xl font-bold tabular-nums text-emerald-800">
                    {workerDetailQ.data.data.totals.rangeBunches}
                  </div>
                  <div className="text-[10px] text-emerald-700">
                    bunches · {workerDetailQ.data.data.range.from} →{" "}
                    {workerDetailQ.data.data.range.to} ·{" "}
                    {workerDetailQ.data.data.totals.recordCount} record(s)
                  </div>
                </div>

                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  OPH attribution history
                </div>
                <div className="space-y-1.5">
                  {workerDetailQ.data.data.attributions.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2 text-xs"
                    >
                      <div>
                        <div className="font-mono font-medium">
                          {a.ophCode ?? `${a.blockCode}-${a.tphCode}`}
                        </div>
                        <div className="text-[10px] text-text-secondary">
                          {a.blockCode} · {a.tphCode} ·{" "}
                          <span
                            className={
                              a.mode === "AI"
                                ? "text-emerald-700"
                                : "text-slate-600"
                            }
                          >
                            {a.mode}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold tabular-nums">
                          {a.bunches} bunches
                        </div>
                        <div className="text-[10px] text-text-secondary">
                          {a.auditDate}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!workerDetailQ.data.data.attributions.length && (
                    <div className="rounded-lg bg-surface-1 p-3 text-center text-[11px] text-text-secondary">
                      No harvest records yet for this worker. Attribution
                      appears here after a TPH audit's OPH card credits
                      their bunches.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Block detail bottom sheet · schedule vs actual + all TPH
          audits for one block ("pick a block, see all activity") */}
      {blockDetailId && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={() => setBlockDetailId(null)}
        >
          <div
            className="mx-auto max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface-0 p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-base font-semibold">
                  Block{" "}
                  {blockDetailQ.data?.data.block.code ??
                    blockCodeById.get(blockDetailId) ??
                    ""}
                </div>
                <div className="text-[11px] text-text-secondary">
                  {blockDetailQ.data?.data.block.hectaresPlanted ?? "—"} ha ·
                  harvest activity
                </div>
              </div>
              <button
                onClick={() => setBlockDetailId(null)}
                className="rounded p-1.5 text-text-secondary active:bg-surface-2"
              >
                <IconX size={18} />
              </button>
            </div>

            {blockDetailQ.isLoading && (
              <div className="py-6 text-center text-xs text-text-secondary">
                Loading…
              </div>
            )}

            {blockDetailQ.data && (
              <>
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <label className="text-[10px] text-text-secondary">
                    From
                    <input
                      type="date"
                      value={blockFrom || blockDetailQ.data.data.range.from}
                      onChange={(e) => setBlockFrom(e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                    />
                  </label>
                  <label className="text-[10px] text-text-secondary">
                    To
                    <input
                      type="date"
                      value={blockTo || blockDetailQ.data.data.range.to}
                      onChange={(e) => setBlockTo(e.target.value)}
                      className="mt-0.5 w-full rounded-lg border border-border bg-surface-0 px-2 py-1.5 text-xs"
                    />
                  </label>
                </div>

                <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-surface-1 p-2.5">
                    <div className="text-lg font-bold tabular-nums">
                      {blockDetailQ.data.data.totals.estimatedBunches}
                    </div>
                    <div className="text-[10px] text-text-secondary">
                      est. bunches
                    </div>
                  </div>
                  <div className="rounded-lg bg-surface-1 p-2.5">
                    <div className="text-lg font-bold tabular-nums text-emerald-700">
                      {blockDetailQ.data.data.totals.actualBunches}
                    </div>
                    <div className="text-[10px] text-text-secondary">
                      actual bunches
                    </div>
                  </div>
                  <div className="rounded-lg bg-surface-1 p-2.5">
                    <div className="text-lg font-bold tabular-nums">
                      {blockDetailQ.data.data.totals.auditedBunches}
                    </div>
                    <div className="text-[10px] text-text-secondary">
                      audited bunches
                    </div>
                  </div>
                </div>

                <div className="mb-1.5 flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                    Schedule · target vs actual
                  </div>
                  <div className="text-[10px] text-text-secondary">
                    {filteredScheduleLines.length}/
                    {blockDetailQ.data.data.scheduleLines.length}
                  </div>
                </div>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {(
                    [
                      ["ALL", "All"],
                      ["DRAFT", "Draft"],
                      ["CONFIRMED", "Confirmed"],
                      ["IN_PROGRESS", "In progress"],
                      ["DONE", "Done"],
                    ] as const
                  ).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setBlockStatusFilter(val)}
                      className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                        blockStatusFilter === val
                          ? "border-text-primary bg-text-primary text-white"
                          : "border-border bg-surface-0 text-text-secondary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="mb-2 text-[10px] text-text-secondary">
                  {STATUS_DESC[blockStatusFilter]}
                </div>
                <div className="mb-3 space-y-1.5">
                  {filteredScheduleLines.map((l) => (
                    <div
                      key={l.id}
                      className="rounded-lg bg-surface-1 px-3 py-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {l.targetHarvestDate ?? "—"}
                          {l.actualHarvestDate
                            ? ` → ${l.actualHarvestDate}`
                            : ""}
                        </span>
                        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[9px] font-semibold">
                          {l.status}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[10px] text-text-secondary">
                        <span>
                          {l.estimatedBunches} est. → {l.actualBunches} actual
                          bunches
                        </span>
                        {l.varianceBunches !== null && (
                          <span
                            className={`font-semibold ${l.varianceBunches >= 0 ? "text-emerald-700" : "text-red-700"}`}
                          >
                            {l.varianceBunches >= 0 ? "+" : ""}
                            {l.varianceBunches} ({l.variancePct}%)
                          </span>
                        )}
                      </div>
                      {l.varianceDays !== null && l.varianceDays !== 0 && (
                        <div className="text-[10px] text-text-secondary">
                          {l.varianceDays > 0
                            ? `${l.varianceDays}d late`
                            : `${Math.abs(l.varianceDays)}d early`}
                        </div>
                      )}
                    </div>
                  ))}
                  {!filteredScheduleLines.length && (
                    <div className="rounded-lg bg-surface-1 p-3 text-center text-[11px] text-text-secondary">
                      {blockDetailQ.data.data.scheduleLines.length
                        ? "No schedule lines match this status."
                        : "No schedule lines in this range."}
                    </div>
                  )}
                </div>

                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  TPH audits · full breakdown for this date range
                </div>
                <div className="space-y-1.5">
                  {filteredAudits.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-lg bg-surface-1 px-3 py-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-medium">
                            {a.ophCode ?? a.tphCode}
                          </span>
                          <span
                            className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${a.mode === "AI" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}
                          >
                            {a.mode}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold tabular-nums">
                            {a.totalBunches} bunches
                          </div>
                          <div className="text-[10px] text-text-secondary">
                            {a.auditDate}
                          </div>
                        </div>
                      </div>
                      {a.harvesters.length > 0 && (
                        <div className="mt-1 text-[10px] text-text-secondary">
                          {a.harvesters
                            .map((h) => `${h.name}:${h.bunches}`)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                  {!filteredAudits.length && (
                    <div className="rounded-lg bg-surface-1 p-3 text-center text-[11px] text-text-secondary">
                      No audits in this range.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const MOBILE_DEFECT_KEYS = [
  "long_stalk",
  "wet",
  "dirty",
  "old",
  "underripe",
  "unfresh",
] as const;

function AiCorrectionSheet({
  ai,
  saving,
  err,
  onSave,
}: {
  ai: {
    total: number;
    ripe: number;
    underripe: number;
    unripe: number;
    overripe: number;
    empty: number;
    rotten: number;
    socketCount: number;
    exocarpColour: string | null;
    mesocarpColour: string | null;
    defectFlags: Record<string, boolean>;
  };
  saving: boolean;
  err: string | null;
  onSave: (patch: {
    ripe: number;
    underripe: number;
    unripe: number;
    overripe: number;
    empty: number;
    rotten: number;
    socketCount: number;
    exocarpColour: string | null;
    mesocarpColour: string | null;
    defectFlags: Record<string, boolean>;
  }) => void;
}) {
  const [ripe, setRipe] = useState(ai.ripe);
  const [underripe, setUnderripe] = useState(ai.underripe);
  const [unripe, setUnripe] = useState(ai.unripe);
  const [overripe, setOverripe] = useState(ai.overripe);
  const [empty, setEmpty] = useState(ai.empty);
  const [rotten, setRotten] = useState(ai.rotten);
  const [socketCount, setSocketCount] = useState(ai.socketCount);
  const [exocarpColour, setExocarpColour] = useState(ai.exocarpColour);
  const [mesocarpColour, setMesocarpColour] = useState(ai.mesocarpColour);
  const [defectFlags, setDefectFlags] = useState<Record<string, boolean>>({
    long_stalk: !!ai.defectFlags?.long_stalk,
    wet: !!ai.defectFlags?.wet,
    dirty: !!ai.defectFlags?.dirty,
    old: !!ai.defectFlags?.old,
    underripe: !!ai.defectFlags?.underripe,
    unfresh: !!ai.defectFlags?.unfresh,
  });
  const sum = ripe + underripe + unripe + overripe + empty + rotten;
  const sumOk = sum === ai.total;

  const row = (
    label: string,
    v: number,
    set: (n: number) => void,
    colour: string,
  ) => (
    <div className="flex items-center gap-2">
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${colour}`}
        aria-hidden
      />
      <span className="flex-1 text-xs">{label}</span>
      <button
        onClick={() => set(Math.max(0, v - 1))}
        className="h-7 w-7 rounded border border-border bg-white text-sm font-bold"
      >
        −
      </button>
      <input
        type="number"
        min={0}
        value={v}
        onChange={(e) => set(Math.max(0, Number(e.target.value) || 0))}
        className="w-12 rounded border border-border bg-white px-1 py-1 text-center text-sm font-mono tabular-nums"
      />
      <button
        onClick={() => set(v + 1)}
        className="h-7 w-7 rounded border border-border bg-white text-sm font-bold"
      >
        +
      </button>
    </div>
  );

  return (
    <div className="space-y-3 rounded-xl border border-emerald-300 bg-white p-3 text-xs">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-emerald-900">Correct AI grading</div>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sumOk ? "bg-emerald-600 text-white" : "bg-red-100 text-red-800"}`}
        >
          Σ {sum}/{ai.total}
        </span>
      </div>

      <div className="space-y-1.5">
        {row("Ripe", ripe, setRipe, "bg-emerald-600")}
        {row("Underripe", underripe, setUnderripe, "bg-amber-500")}
        {row("Unripe", unripe, setUnripe, "bg-blue-500")}
        {row("Overripe", overripe, setOverripe, "bg-orange-600")}
        {row("Empty", empty, setEmpty, "bg-slate-400")}
        {row("Rotten", rotten, setRotten, "bg-red-600")}
      </div>

      <label className="block">
        <span className="text-text-secondary">
          Total sockets ({ai.total
            ? (socketCount / ai.total).toFixed(1)
            : "0"}
          /bunch)
        </span>
        <input
          type="number"
          min={0}
          value={socketCount}
          onChange={(e) => setSocketCount(Math.max(0, Number(e.target.value) || 0))}
          className="mt-0.5 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-mono"
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-text-secondary">Outer layer</span>
          <select
            value={exocarpColour ?? ""}
            onChange={(e) => setExocarpColour(e.target.value || null)}
            className="mt-0.5 w-full rounded border border-border bg-white px-2 py-1.5 text-xs"
          >
            <option value="">—</option>
            <option value="black">black</option>
            <option value="dark_purple">dark_purple</option>
            <option value="reddish">reddish</option>
            <option value="orange">orange</option>
          </select>
        </label>
        <label className="block">
          <span className="text-text-secondary">Mesocarp</span>
          <select
            value={mesocarpColour ?? ""}
            onChange={(e) => setMesocarpColour(e.target.value || null)}
            className="mt-0.5 w-full rounded border border-border bg-white px-2 py-1.5 text-xs"
          >
            <option value="">—</option>
            <option value="yellow">yellow</option>
            <option value="yellow_orange">yellow_orange</option>
            <option value="orange">orange</option>
          </select>
        </label>
      </div>

      <div>
        <div className="mb-1 text-text-secondary">Defect flags</div>
        <div className="grid grid-cols-2 gap-1">
          {MOBILE_DEFECT_KEYS.map((k) => (
            <label key={k} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!defectFlags[k]}
                onChange={(e) =>
                  setDefectFlags({ ...defectFlags, [k]: e.target.checked })
                }
              />
              <span>{k.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      </div>

      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
          {err}
        </div>
      )}

      <button
        disabled={saving || !sumOk}
        onClick={() =>
          onSave({
            ripe,
            underripe,
            unripe,
            overripe,
            empty,
            rotten,
            socketCount,
            exocarpColour,
            mesocarpColour,
            defectFlags,
          })
        }
        className="w-full rounded-lg bg-emerald-700 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save correction"}
      </button>
    </div>
  );
}

/**
 * Mandor's recent bunch audits · mobile mirror of the Smart Grading table.
 * Only shows this Mandor's own audits (PIC-scoped) — audits done by other
 * Mandors are hidden here; the office (/smart-grading) view sees everyone.
 * Rows are read-only in this list; the primary edit path stays the
 * inline correction sheet immediately after AI grading. To full-edit an
 * older audit, use the desktop /smart-grading page.
 */
function MandorAuditsList({
  audits,
  tphCodeById,
  workerById,
  harvesterIds,
  currentUserId,
  onRefresh,
}: {
  audits: AuditBatch[];
  tphCodeById: Map<string, string>;
  workerById: Map<string, { id: string; name: string; workerType?: string }>;
  harvesterIds: string[];
  currentUserId: string | null;
  onRefresh: () => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const [rejectErr, setRejectErr] = useState<string | null>(null);

  // ── Filters ─────────────────────────────────────────────────────────
  const [filterDate, setFilterDate] = useState<
    "TODAY" | "YESTERDAY" | "LAST7" | "ALL"
  >("TODAY");
  const [filterMandor, setFilterMandor] = useState<string>("ALL");
  const [filterTph, setFilterTph] = useState<string>("ALL");
  const [filterTime, setFilterTime] = useState<
    "ALL" | "MORNING" | "AFTERNOON" | "EVENING"
  >("ALL");
  const [filterHarvester, setFilterHarvester] = useState<string>("ALL");

  const doReject = async (auditId: string) => {
    setRejectErr(null);
    if (!rejectReason.trim()) {
      setRejectErr("Enter a reason (e.g. duplicate / wrong TPH).");
      return;
    }
    setRejectBusy(true);
    try {
      await api.patch(`/v1/bunch-audits/${auditId}`, {
        rejected: true,
        rejection_reason: rejectReason.trim(),
      });
      setRejectingId(null);
      setRejectReason("");
      onRefresh();
    } catch (err) {
      setRejectErr(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRejectBusy(false);
    }
  };

  // ── Base scope: PIC-scoped (this user's own audits or legacy) ──
  const scoped = audits.filter(
    (a) => !a.submittedByUserId || a.submittedByUserId === currentUserId,
  );

  // Build unique dropdown option lists from what actually exists in scope.
  const uniqueMandors = Array.from(
    new Set(scoped.map((a) => a.mandorWorkerId).filter(Boolean) as string[]),
  );
  const uniqueTphs = Array.from(new Set(scoped.map((a) => a.tphId)));

  // Apply filters.
  const today = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const dayStart = startOfDay(today);
  const dayEnd = new Date(dayStart.getTime() + 86_400_000);
  const yStart = new Date(dayStart.getTime() - 86_400_000);
  const last7Start = new Date(dayStart.getTime() - 6 * 86_400_000);

  const mine = scoped
    .filter((a) => {
      const d = new Date(a.auditDate);
      // Date
      if (filterDate === "TODAY" && (d < dayStart || d >= dayEnd)) return false;
      if (filterDate === "YESTERDAY" && (d < yStart || d >= dayStart))
        return false;
      if (filterDate === "LAST7" && d < last7Start) return false;
      // Mandor
      if (filterMandor !== "ALL" && a.mandorWorkerId !== filterMandor)
        return false;
      // TPH
      if (filterTph !== "ALL" && a.tphId !== filterTph) return false;
      // Time slot
      if (filterTime !== "ALL") {
        const h = d.getHours();
        if (filterTime === "MORNING" && !(h >= 6 && h < 12)) return false;
        if (filterTime === "AFTERNOON" && !(h >= 12 && h < 18)) return false;
        if (filterTime === "EVENING" && !(h >= 18 || h < 6)) return false;
      }
      // Harvester · match if the current gang roster includes the picked
      // harvester (audit itself is not directly tied to one harvester —
      // it's a whole-gang activity — so this filters to audits that came
      // from a gang the harvester belongs to today).
      if (filterHarvester !== "ALL") {
        if (!harvesterIds.includes(filterHarvester)) return false;
        if (!a.gangId) return false;
      }
      return true;
    })
    .slice(0, 50);

  const filterBar = (
    <div className="rounded-xl border border-border bg-surface-1 p-3 space-y-2">
      <div className="text-sm font-semibold uppercase text-text-secondary">
        Filter
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <label className="block">
          <span className="text-text-secondary">Date</span>
          <select
            value={filterDate}
            onChange={(e) =>
              setFilterDate(e.target.value as typeof filterDate)
            }
            className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm"
          >
            <option value="TODAY">Today</option>
            <option value="YESTERDAY">Yesterday</option>
            <option value="LAST7">Last 7 days</option>
            <option value="ALL">All time</option>
          </select>
        </label>
        <label className="block">
          <span className="text-text-secondary">Time of day</span>
          <select
            value={filterTime}
            onChange={(e) =>
              setFilterTime(e.target.value as typeof filterTime)
            }
            className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm"
          >
            <option value="ALL">All hours</option>
            <option value="MORNING">Morning · 6-12</option>
            <option value="AFTERNOON">Afternoon · 12-18</option>
            <option value="EVENING">Evening · 18-6</option>
          </select>
        </label>
        <label className="block">
          <span className="text-text-secondary">TPH</span>
          <select
            value={filterTph}
            onChange={(e) => setFilterTph(e.target.value)}
            className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm font-mono"
          >
            <option value="ALL">All TPH</option>
            {uniqueTphs.map((id) => (
              <option key={id} value={id}>
                {tphCodeById.get(id) ?? id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-text-secondary">Mandor</span>
          <select
            value={filterMandor}
            onChange={(e) => setFilterMandor(e.target.value)}
            className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm"
          >
            <option value="ALL">All Mandors</option>
            {uniqueMandors.map((id) => (
              <option key={id} value={id}>
                {workerById.get(id)?.name ?? id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
        <label className="col-span-2 block">
          <span className="text-text-secondary">Harvester (in your team)</span>
          <select
            value={filterHarvester}
            onChange={(e) => setFilterHarvester(e.target.value)}
            className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm"
          >
            <option value="ALL">All harvesters</option>
            {harvesterIds.map((id) => (
              <option key={id} value={id}>
                {workerById.get(id)?.name ?? id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {(filterDate !== "TODAY" ||
        filterMandor !== "ALL" ||
        filterTph !== "ALL" ||
        filterTime !== "ALL" ||
        filterHarvester !== "ALL") && (
        <button
          onClick={() => {
            setFilterDate("TODAY");
            setFilterMandor("ALL");
            setFilterTph("ALL");
            setFilterTime("ALL");
            setFilterHarvester("ALL");
          }}
          className="w-full rounded border border-border bg-surface-2 py-2 text-sm font-semibold text-text-secondary hover:bg-surface-0"
        >
          Clear filters
        </button>
      )}
    </div>
  );

  if (!mine.length) {
    return (
      <div className="space-y-2">
        {filterBar}
        <div className="rounded-xl border border-dashed border-border p-4 text-center text-[11px] text-text-secondary">
          No bunch audits match the current filters.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold text-brand-charcoal">
          Bunch audits
        </div>
        <div className="text-sm text-text-secondary">
          {mine.length} · PIC-scoped
        </div>
      </div>
      {filterBar}
      <div className="space-y-1.5">
        {mine.map((a) => {
          const editable =
            !a.submittedByUserId || a.submittedByUserId === currentUserId;
          const rejection = a.rgcqRawSignals?.rejection;
          const isRejected = !!rejection;
          const isExpanded = expandedId === a.id;
          const isRejecting = rejectingId === a.id;
          return (
            <div
              key={a.id}
              className={`rounded-lg border p-3 text-sm ${
                isRejected
                  ? "border-red-300 bg-red-50 opacity-70"
                  : "border-border bg-white"
              }`}
            >
              {isRejected && (
                <div className="mb-2 rounded bg-red-600 px-2 py-1 text-xs font-bold text-white">
                  REJECTED · {rejection.reason}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono">
                    {new Date(a.auditDate).toLocaleTimeString("en-MY", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-bold ${a.mode === "AI" ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"}`}
                  >
                    {a.mode}
                  </span>
                  <span className="text-text-secondary">
                    · {tphCodeById.get(a.tphId) ?? "?"}
                  </span>
                </div>
                {a.readyForHarvest && !isRejected && (
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-bold ${
                      a.readyForHarvest === "YES"
                        ? "bg-emerald-100 text-emerald-800"
                        : a.readyForHarvest === "MIXED"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {a.readyForHarvest}
                  </span>
                )}
              </div>

              {/* Bunch counts · one labelled row per class for clarity */}
              <div className={`mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-sm ${isRejected ? "line-through" : ""}`}>
                <div className="col-span-2 text-sm">
                  <b className="text-text-primary text-base">
                    {a.totalBunches}
                  </b>{" "}
                  <span className="text-text-secondary">total bunches</span>
                </div>
                <div>
                  <span className="text-emerald-700 font-bold">
                    {a.ripeCount}
                  </span>{" "}
                  <span className="text-text-secondary">Ripe</span>
                </div>
                <div>
                  <span className="text-blue-600 font-bold">
                    {a.unripeCount}
                  </span>{" "}
                  <span className="text-text-secondary">Unripe</span>
                </div>
                <div>
                  <span className="text-orange-700 font-bold">
                    {a.overripeCount}
                  </span>{" "}
                  <span className="text-text-secondary">Overripe</span>
                </div>
                <div>
                  <span className="text-slate-600 font-bold">
                    {a.emptyCount}
                  </span>{" "}
                  <span className="text-text-secondary">Empty</span>
                </div>
                <div className="col-span-2 mt-1 flex items-center justify-between border-t border-border pt-1 text-sm">
                  <span className="text-text-secondary">Sockets</span>
                  <span className="font-mono">{a.socketCount}</span>
                </div>
              </div>

              {(a.exocarpColour || a.mesocarpColour) && (
                <div className="mt-2 grid grid-cols-2 gap-x-2 text-sm">
                  <div>
                    <span className="text-text-secondary">Outer layer</span>
                    <div className="font-mono">{a.exocarpColour ?? "—"}</div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Mesocarp</span>
                    <div className="font-mono">{a.mesocarpColour ?? "—"}</div>
                  </div>
                </div>
              )}

              {(() => {
                const qs = a.rgcqRawSignals?.quality_state_counts ?? {};
                const parts = Object.entries(qs)
                  .filter(([, n]) => (n ?? 0) > 0)
                  .map(([k, n]) => ({ label: k.replace(/_/g, " "), n }));
                if (!parts.length) return null;
                return (
                  <div className="mt-2 rounded bg-emerald-50 p-2 text-sm">
                    <div className="mb-1 text-xs font-semibold text-emerald-900">
                      Quality state
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-sm">
                      {parts.map(({ label, n }) => (
                        <div
                          key={label}
                          className="flex justify-between text-emerald-900"
                        >
                          <span className="capitalize">{label}</span>
                          <span className="font-mono font-bold">{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Row actions · always visible when the row is editable and
                  not currently rejected. Rejected rows show View only. */}
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() =>
                    setExpandedId(isExpanded ? null : a.id)
                  }
                  className="flex-1 rounded border border-emerald-600 bg-white py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                >
                  {isExpanded ? "Hide details" : "View / edit"}
                </button>
                {!isRejected && editable && (
                  <button
                    onClick={() => {
                      setRejectingId(isRejecting ? null : a.id);
                      setRejectReason("");
                      setRejectErr(null);
                    }}
                    className="flex-1 rounded border border-red-300 bg-white py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                  >
                    {isRejecting ? "Cancel reject" : "Reject"}
                  </button>
                )}
              </div>

              {/* Expanded per-bunch editor — same signal-by-signal
                  interface as the desktop Smart Grading modal. */}
              {isExpanded && (
                <MobileBunchEditor
                  audit={a}
                  editable={editable && !isRejected}
                  onSaved={() => {
                    setExpandedId(null);
                    onRefresh();
                  }}
                />
              )}

              {/* Inline reject sheet. */}
              {isRejecting && !isRejected && (
                <div className="mt-2 rounded border border-red-300 bg-red-50 p-2 space-y-2">
                  <div className="text-sm font-semibold text-red-800">
                    Reject this audit
                  </div>
                  <textarea
                    rows={2}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason · e.g. duplicate submission / wrong TPH / test entry"
                    className="w-full rounded border border-red-300 bg-white p-2 text-sm"
                  />
                  {rejectErr && (
                    <div className="text-sm text-red-800">{rejectErr}</div>
                  )}
                  <button
                    onClick={() => void doReject(a.id)}
                    disabled={rejectBusy}
                    className="w-full rounded bg-red-600 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {rejectBusy ? "Rejecting…" : "Confirm reject"}
                  </button>
                </div>
              )}

              {!editable && (
                <div className="mt-2 rounded bg-slate-100 px-2 py-1 text-sm text-slate-700">
                  🔒 Read-only · not your PIC scope
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Mobile · per-bunch editor. Renders the same OLC / mesocarp / socket
 * dropdowns as the desktop Smart Grading modal, plus add/remove buttons
 * and a live classifier chip on every row. Saves via PATCH with the
 * per_bunch payload — server re-runs the flowchart classifier.
 */
interface MobileEditRow {
  bunch_index: number;
  socket_bucket: string;
  olc: Olc | null;
  mesocarp: Mesocarp | null;
}

const MOBILE_QUALITY_KEYS = [
  "fresh",
  "underripe",
  "long_stalk",
  "old",
  "wet",
  "dirty",
  "dura",
  "parthenocarpy",
  "rotten",
] as const;
type MobileQualityKey = (typeof MOBILE_QUALITY_KEYS)[number];
const MOBILE_QUALITY_LABELS: Record<MobileQualityKey, string> = {
  fresh: "Fresh",
  underripe: "Underripe",
  long_stalk: "Long stalk",
  old: "Old",
  wet: "Wet",
  dirty: "Dirt / cont.",
  dura: "Dura",
  parthenocarpy: "Parthenocarpy",
  rotten: "Rotten",
};

function MobileBunchEditor({
  audit,
  editable,
  onSaved,
}: {
  audit: AuditBatch;
  editable: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [qualityCounts, setQualityCounts] = useState<
    Record<MobileQualityKey, number>
  >(() => {
    const src = audit.rgcqRawSignals?.quality_state_counts ?? {};
    const seed = {} as Record<MobileQualityKey, number>;
    for (const k of MOBILE_QUALITY_KEYS) seed[k] = Number(src[k] ?? 0);
    return seed;
  });
  const [rows, setRows] = useState<MobileEditRow[]>(() => {
    const src = audit.rgcqRawSignals?.per_bunch ?? [];
    if (src.length) {
      return src.map((b) => ({
        bunch_index: b.bunch_index,
        socket_bucket: socketBucketFor(b.detached_sockets),
        olc: (b.olc ?? null) as Olc | null,
        mesocarp: (b.mesocarp ?? null) as Mesocarp | null,
      }));
    }
    return Array.from({ length: audit.totalBunches }, (_, i) => ({
      bunch_index: i + 1,
      socket_bucket: socketBucketFor(
        Math.round((audit.socketCount ?? 0) / (audit.totalBunches || 1)),
      ),
      olc: (audit.exocarpColour ?? null) as Olc | null,
      mesocarp: (audit.mesocarpColour ?? null) as Mesocarp | null,
    }));
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upd = (idx: number, patch: Partial<MobileEditRow>) =>
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  const remove = (idx: number) =>
    setRows((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, bunch_index: i + 1 })),
    );
  const add = () =>
    setRows((prev) => [
      ...prev,
      {
        bunch_index: prev.length + 1,
        socket_bucket: "gte5",
        olc: null,
        mesocarp: null,
      },
    ]);

  const liveCounts: Record<RipenessClass, number> = {
    RIPE: 0,
    UNRIPE: 0,
    OVERRIPE: 0,
    EMPTY: 0,
  };
  for (const r of rows) {
    const bucket = SOCKET_BUCKETS.find((b) => b.key === r.socket_bucket)!;
    const cls = classifyBunch({
      detachedSockets: bucket.sockets,
      detachedPct: bucket.pct,
      olc: r.olc,
      mesocarp: r.mesocarp,
    });
    liveCounts[cls.ripeness]++;
  }

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const per_bunch = rows.map((r) => {
        const bucket = SOCKET_BUCKETS.find((b) => b.key === r.socket_bucket)!;
        return {
          bunch_index: r.bunch_index,
          detached_sockets: bucket.sockets,
          detached_pct: typeof bucket.pct === "number" ? bucket.pct : -1,
          olc: r.olc,
          mesocarp: r.mesocarp,
          defect_flags: {},
        };
      });
      await api.patch(`/v1/bunch-audits/${audit.id}`, {
        per_bunch,
        quality_state_counts: qualityCounts,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded border border-border bg-surface-1 p-3 text-sm">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="font-semibold text-brand-charcoal">
          Per-bunch · ripeness matrix
        </div>
        {editable && (
          <button
            onClick={() => setEditing((v) => !v)}
            className="rounded border border-brand-green bg-white px-1.5 py-0.5 text-[9px] font-semibold text-brand-green-dark hover:bg-brand-green hover:text-white"
          >
            {editing ? "View mode" : "Edit"}
          </button>
        )}
      </div>

      {editing && rows.length !== audit.totalBunches && (
        <div className="mb-1.5 rounded bg-amber-50 border border-amber-300 p-1 text-[9px] text-amber-900">
          AI counted {audit.totalBunches} · you have {rows.length}. Saving
          overrides the AI count with the correct quantity.
        </div>
      )}

      {/* Quality-state · per-batch bunch counts (orthogonal to ripeness) */}
      {editing && (
        <div className="mt-1.5 rounded border border-border bg-white p-1.5">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-[10px] font-semibold text-brand-charcoal">
              Quality state · bunch counts
            </div>
            <span className="text-[9px] text-text-secondary">
              Σ{" "}
              {MOBILE_QUALITY_KEYS.reduce(
                (n, k) => n + qualityCounts[k],
                0,
              )}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {MOBILE_QUALITY_KEYS.map((k) => (
              <label
                key={k}
                className="flex items-center justify-between rounded border border-border bg-surface-1 px-1 py-0.5 text-[10px]"
              >
                <span className="text-text-secondary">
                  {MOBILE_QUALITY_LABELS[k]}
                </span>
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() =>
                      setQualityCounts((p) => ({
                        ...p,
                        [k]: Math.max(0, p[k] - 1),
                      }))
                    }
                    className="h-5 w-5 rounded border border-border bg-white text-[10px] font-bold"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={0}
                    value={qualityCounts[k]}
                    onChange={(e) =>
                      setQualityCounts((p) => ({
                        ...p,
                        [k]: Math.max(0, Number(e.target.value) || 0),
                      }))
                    }
                    className="w-8 rounded border border-border bg-white px-0.5 py-0.5 text-center text-[10px] font-mono tabular-nums"
                  />
                  <button
                    onClick={() =>
                      setQualityCounts((p) => ({ ...p, [k]: p[k] + 1 }))
                    }
                    className="h-5 w-5 rounded border border-border bg-white text-[10px] font-bold"
                  >
                    +
                  </button>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1">
        {rows.map((r, idx) => {
          const bucket = SOCKET_BUCKETS.find((b) => b.key === r.socket_bucket)!;
          const cls = classifyBunch({
            detachedSockets: bucket.sockets,
            detachedPct: bucket.pct,
            olc: r.olc,
            mesocarp: r.mesocarp,
          });
          const badgeClass =
            cls.ripeness === "RIPE"
              ? "bg-emerald-600 text-white"
              : cls.ripeness === "UNRIPE"
                ? "bg-blue-500 text-white"
                : cls.ripeness === "OVERRIPE"
                  ? "bg-orange-600 text-white"
                  : "bg-slate-500 text-white";
          return editing ? (
            <div
              key={r.bunch_index}
              className="rounded border border-border bg-white p-1.5"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px]">#{r.bunch_index}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${badgeClass}`}
                >
                  {cls.ripeness}
                </span>
                <button
                  onClick={() => remove(idx)}
                  disabled={rows.length <= 1}
                  className="text-text-secondary hover:text-red-700 text-sm disabled:opacity-30"
                  title="Remove bunch"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-1 gap-1">
                <select
                  value={r.olc ?? ""}
                  onChange={(e) =>
                    upd(idx, { olc: (e.target.value || null) as Olc | null })
                  }
                  className="w-full rounded border border-border bg-surface-2 px-1 py-1 text-[10px]"
                >
                  <option value="">— outer layer colour —</option>
                  {OLC_UI_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  value={r.mesocarp ?? ""}
                  onChange={(e) =>
                    upd(idx, {
                      mesocarp: (e.target.value || null) as Mesocarp | null,
                    })
                  }
                  className="w-full rounded border border-border bg-surface-2 px-1 py-1 text-[10px]"
                >
                  <option value="">— mesocarp —</option>
                  {MESOCARP_UI_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  value={r.socket_bucket}
                  onChange={(e) => upd(idx, { socket_bucket: e.target.value })}
                  className="w-full rounded border border-border bg-surface-2 px-1 py-1 text-[10px] font-mono"
                >
                  {SOCKET_BUCKETS.map((b) => (
                    <option key={b.key} value={b.key}>
                      Sockets: {b.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div
              key={r.bunch_index}
              className="flex items-center justify-between rounded bg-white px-2 py-1"
            >
              <span className="font-mono">#{r.bunch_index}</span>
              <span className="text-text-secondary flex-1 ml-2">
                {bucket.label}s · {r.olc ?? "—"} / {r.mesocarp ?? "—"}
              </span>
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${badgeClass}`}
              >
                {cls.ripeness}
              </span>
            </div>
          );
        })}
      </div>

      {editing && (
        <>
          <button
            onClick={add}
            className="mt-1.5 w-full rounded border border-brand-green bg-white py-1 text-[10px] font-semibold text-brand-green-dark hover:bg-brand-green hover:text-white"
          >
            + Add bunch
          </button>
          <div className="mt-1.5 text-[9px] text-text-secondary">
            Live aggregate · RIPE {liveCounts.RIPE} · UNRIPE {liveCounts.UNRIPE}
            · OVERRIPE {liveCounts.OVERRIPE} · EMPTY {liveCounts.EMPTY}
          </div>
          {err && (
            <div className="mt-1 text-[10px] text-red-700">{err}</div>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="mt-1 w-full rounded bg-brand-green py-1.5 text-[10px] font-bold text-white hover:bg-brand-green-dark disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save corrections"}
          </button>
        </>
      )}
    </div>
  );
}
